/* ==========================================================================
   Whiskerwick Warmth — theme behaviour
   No dependencies. Loaded with `defer`, so the DOM is ready when this runs.
   Every feature degrades gracefully if its markup isn't on the page.
   ========================================================================== */

(function () {
  'use strict';

  var announce = function (message) {
    var region = document.getElementById('a11y-status');
    if (region) region.textContent = message;
  };

  var formatMoney = function (cents) {
    // Shopify exposes the shop's money format via the cart; fall back to a
    // simple 2dp render so the price never shows raw cents.
    var value = (cents / 100).toFixed(2);
    var format = window.Whiskerwick && window.Whiskerwick.moneyFormat;
    if (format) {
      return format
        .replace(/\{\{\s*amount\s*\}\}/, value)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace('.', ','));
    }
    return value;
  };

  /* ------------------------------------------------------------------
     Mobile navigation
     ------------------------------------------------------------------ */

  function initMobileNav() {
    var toggle = document.querySelector('[data-nav-toggle]');
    var nav = document.querySelector('[data-mobile-nav]');
    if (!toggle || !nav) return;

    var panel = nav.querySelector('[data-mobile-nav-panel]');
    var lastFocused = null;

    // Keep the drawer at viewport level. Some Shopify preview wrappers make a
    // fixed element inside the sticky header use the header's height, which
    // leaves the links hidden behind the page hero.
    if (nav.parentElement !== document.body) document.body.appendChild(nav);

    function open() {
      lastFocused = document.activeElement;
      nav.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('mobile-nav-is-open');
      var firstLink = panel.querySelector('a, button');
      if (firstLink) firstLink.focus();
    }

    function close() {
      nav.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('mobile-nav-is-open');
      if (lastFocused) lastFocused.focus();
    }

    toggle.addEventListener('click', open);

    nav.querySelectorAll('[data-nav-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && !nav.hidden) close();
    });

    // Keep focus inside the panel while it's open.
    nav.addEventListener('keydown', function (event) {
      if (event.key !== 'Tab' || nav.hidden) return;
      var focusable = panel.querySelectorAll('a[href], button:not([disabled]), summary, input');
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  /* ------------------------------------------------------------------
     Desktop dropdowns — close on outside click / Escape
     ------------------------------------------------------------------ */

  function initNavDropdowns() {
    var menus = document.querySelectorAll('[data-nav-details]');
    if (!menus.length) return;

    document.addEventListener('click', function (event) {
      menus.forEach(function (menu) {
        if (menu.open && !menu.contains(event.target)) menu.open = false;
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      menus.forEach(function (menu) {
        if (menu.open) {
          menu.open = false;
          var summary = menu.querySelector('summary');
          if (summary) summary.focus();
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     Quantity steppers
     ------------------------------------------------------------------ */

  function initQuantity() {
    document.querySelectorAll('[data-quantity]').forEach(function (wrapper) {
      var input = wrapper.querySelector('input');
      if (!input) return;

      var step = function (delta) {
        var min = parseInt(input.getAttribute('min') || '0', 10);
        var next = (parseInt(input.value, 10) || 0) + delta;
        if (next < min) next = min;
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };

      var minus = wrapper.querySelector('[data-quantity-minus]');
      var plus = wrapper.querySelector('[data-quantity-plus]');
      if (minus) minus.addEventListener('click', function () { step(-1); });
      if (plus) plus.addEventListener('click', function () { step(1); });
    });
  }

  /* ------------------------------------------------------------------
     Product: variant picker
     ------------------------------------------------------------------ */

  function initVariantPicker() {
    var form = document.querySelector('[data-product-form]');
    var dataEl = document.querySelector('[data-product-json]');
    if (!form || !dataEl) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (error) {
      return;
    }
    if (!variants || !variants.length) return;

    var picker = form.querySelector('[data-variant-picker]');
    if (!picker) return;

    var idInput = form.querySelector('[data-variant-id]');
    var addButton = form.querySelector('[data-add-button]');
    var addButtonText = form.querySelector('[data-add-button-text]');
    var priceWrapper = document.querySelector('[data-price-wrapper]');

    var soldOutText = addButton ? addButton.getAttribute('data-sold-out-text') : null;
    var addToCartText = addButton ? addButton.getAttribute('data-add-to-cart-text') : null;
    var unavailableText = addButton ? addButton.getAttribute('data-unavailable-text') : null;

    function selectedOptions() {
      return Array.prototype.map.call(
        picker.querySelectorAll('input[type="radio"]:checked'),
        function (input) { return input.value; }
      );
    }

    function findVariant(options) {
      return variants.filter(function (variant) {
        return options.every(function (value, index) {
          return variant.options[index] === value;
        });
      })[0];
    }

    function update() {
      var options = selectedOptions();
      var variant = findVariant(options);

      // Mark option values that lead to no purchasable variant.
      picker.querySelectorAll('input[type="radio"]').forEach(function (input) {
        var index = parseInt(input.getAttribute('data-option-index'), 10);
        var candidate = options.slice();
        candidate[index] = input.value;
        var match = findVariant(candidate);
        var label = input.nextElementSibling;
        if (label) label.classList.toggle('is-unavailable', !match || !match.available);
      });

      if (!variant) {
        if (idInput) idInput.disabled = true;
        if (addButton) {
          addButton.disabled = true;
          if (addButtonText) addButtonText.textContent = unavailableText || 'Unavailable';
        }
        return;
      }

      if (idInput) {
        idInput.value = variant.id;
        idInput.disabled = false;
      }

      if (addButton) {
        addButton.disabled = !variant.available;
        if (addButtonText) {
          addButtonText.textContent = variant.available
            ? (addToCartText || 'Add to basket')
            : (soldOutText || 'Sold out');
        }
      }

      updatePhotoSlots(options, variant);

      if (priceWrapper) {
        var current = priceWrapper.querySelector('.price__current');
        var compare = priceWrapper.querySelector('.price__compare');
        if (current) current.textContent = formatMoney(variant.price);
        if (compare) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compare.textContent = formatMoney(variant.compare_at_price);
            compare.hidden = false;
          } else {
            compare.hidden = true;
          }
        }
      }

      // Keep the URL shareable without reloading the page.
      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    picker.addEventListener('change', update);
    update();
  }

  function petCountFromSelection(options, variant) {
    var values = (options || []).slice();
    if (variant && variant.title) values.push(variant.title);
    for (var index = 0; index < values.length; index += 1) {
      var match = String(values[index]).match(/(?:^|\D)([1-4])(?:\D|$)/);
      if (match) return parseInt(match[1], 10);
    }
    return 1;
  }

  function updatePhotoSlots(options, variant) {
    var personalisation = document.querySelector('[data-personalisation]');
    if (!personalisation) return;

    var petCount = petCountFromSelection(options, variant);
    var slots = personalisation.querySelectorAll('[data-photo-slot]');
    var guidance = personalisation.querySelector('[data-photo-guidance]');
    var addButton = personalisation.querySelector('[data-add-photo]');

    slots.forEach(function (slot) {
      var slotNumber = parseInt(slot.getAttribute('data-photo-slot'), 10);
      var input = slot.querySelector('[data-photo-input]');
      var hasFile = !!(input && input.files && input.files.length);
      var required = personalisation.getAttribute('data-require-photos') === 'true' && slotNumber <= petCount;
      slot.hidden = !required && !hasFile && slot.getAttribute('data-extra-visible') !== 'true';
      if (input) input.required = required;

      var requiredLabel = slot.querySelector('[data-photo-required-label]');
      var optionalLabel = slot.querySelector('[data-photo-optional-label]');
      if (requiredLabel) requiredLabel.hidden = !required;
      if (optionalLabel) optionalLabel.hidden = required;
    });

    if (guidance) {
      guidance.textContent = petCount === 1
        ? 'Add one clear photo of your pet. You can add extra reference angles below.'
        : 'Add one clear photo for each of your ' + petCount + ' pets, matching the name order above.';
    }
    if (addButton) addButton.hidden = !personalisation.querySelector('[data-photo-slot][hidden]');
  }

  function initPhotoSlots() {
    document.querySelectorAll('[data-personalisation]').forEach(function (personalisation) {
      if (personalisation.getAttribute('data-photo-slots-ready') === 'true') return;
      personalisation.setAttribute('data-photo-slots-ready', 'true');

      var addButton = personalisation.querySelector('[data-add-photo]');
      if (addButton) {
        addButton.addEventListener('click', function () {
          var next = personalisation.querySelector('[data-photo-slot][hidden]');
          if (!next) return;
          next.hidden = false;
          next.setAttribute('data-extra-visible', 'true');
          var input = next.querySelector('[data-photo-input]');
          if (input) input.focus();
          addButton.hidden = !personalisation.querySelector('[data-photo-slot][hidden]');
          announce('Another reference photo field is ready.');
        });
      }

      var initialCount = parseInt(personalisation.getAttribute('data-initial-pet-count'), 10) || 1;
      updatePhotoSlots([String(initialCount)], { title: String(initialCount) });
    });
  }

  /* ------------------------------------------------------------------
     Product: photo uploader
     ------------------------------------------------------------------ */

  function initUploader() {
    document.querySelectorAll('[data-uploader]').forEach(function (uploader) {
      if (uploader.getAttribute('data-uploader-ready') === 'true') return;
      uploader.setAttribute('data-uploader-ready', 'true');
      var input = uploader.querySelector('[data-uploader-input]');
      var drop = uploader.querySelector('[data-uploader-drop]');
      var preview = uploader.querySelector('[data-uploader-preview]');
      var thumb = uploader.querySelector('[data-uploader-thumb]');
      var filename = uploader.querySelector('[data-uploader-filename]');
      var clear = uploader.querySelector('[data-uploader-clear]');
      var error = uploader.querySelector('[data-uploader-error]');
      if (!input) return;

      var maxBytes = parseInt(uploader.getAttribute('data-max-bytes'), 10) || 0;
      var maxMb = uploader.getAttribute('data-max-mb') || '20';

      function showError(message) {
        if (!error) return;
        error.textContent = message;
        error.hidden = false;
        input.setCustomValidity(message);
      }

      function clearError() {
        if (error) {
          error.textContent = '';
          error.hidden = true;
        }
        input.setCustomValidity('');
      }

      function reset() {
        input.value = '';
        clearError();
        if (preview) preview.hidden = true;
        if (thumb) thumb.removeAttribute('src');
        if (filename) filename.textContent = '';
      }

      function handleFile(file) {
        clearError();
        if (!file) return reset();

        if (maxBytes && file.size > maxBytes) {
          showError('That file is ' + (file.size / 1048576).toFixed(1) + ' MB. Please choose one under ' + maxMb + ' MB.');
          input.value = '';
          if (preview) preview.hidden = true;
          return;
        }

        if (file.type && file.type.indexOf('image/') !== 0) {
          showError('Please choose an image file.');
          input.value = '';
          if (preview) preview.hidden = true;
          return;
        }

        if (filename) filename.textContent = file.name;
        if (preview) preview.hidden = false;

        // HEIC files often can't be previewed in-browser; the upload still works.
        if (thumb && window.URL && /image\/(jpeg|png|webp|gif)/.test(file.type)) {
          var url = URL.createObjectURL(file);
          thumb.src = url;
          thumb.onload = function () { URL.revokeObjectURL(url); };
          thumb.style.display = '';
        } else if (thumb) {
          thumb.style.display = 'none';
        }

        announce('Photo attached: ' + file.name);
      }

      input.addEventListener('change', function () {
        handleFile(input.files && input.files[0]);
      });

      if (clear) {
        clear.addEventListener('click', function () {
          reset();
          input.focus();
        });
      }

      if (drop) {
        ['dragenter', 'dragover'].forEach(function (name) {
          drop.addEventListener(name, function (event) {
            event.preventDefault();
            uploader.classList.add('is-dragging');
          });
        });

        ['dragleave', 'drop'].forEach(function (name) {
          drop.addEventListener(name, function (event) {
            event.preventDefault();
            uploader.classList.remove('is-dragging');
          });
        });

        drop.addEventListener('drop', function (event) {
          var files = event.dataTransfer && event.dataTransfer.files;
          if (!files || !files.length) return;
          try {
            var transfer = new DataTransfer();
            transfer.items.add(files[0]);
            input.files = transfer.files;
            handleFile(input.files[0]);
          } catch (error) {
            showError('Drag and drop is not available in this browser. Choose the photo using the button instead.');
          }
        });
      }
    });
  }

  /* ------------------------------------------------------------------
     Product: form submit guard
     Files must go through a standard form POST — Shopify does not accept
     line-item file properties over the AJAX cart API — so this only blocks
     submission when something is genuinely missing.
     ------------------------------------------------------------------ */

  function initProductForm() {
    var form = document.querySelector('[data-product-form]');
    if (!form) return;

    var errorEl = form.querySelector('[data-form-error]');
    var button = form.querySelector('[data-add-button]');

    form.addEventListener('submit', function (event) {
      if (!form.checkValidity()) {
        event.preventDefault();
        var invalid = form.querySelector(':invalid');
        if (invalid) {
          invalid.focus();
          if (errorEl) {
            errorEl.textContent = invalid.validationMessage || 'Please complete the highlighted field.';
            errorEl.hidden = false;
          }
        }
        return;
      }

      if (errorEl) errorEl.hidden = true;
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      }
    });
  }

  /* ------------------------------------------------------------------
     Product: media thumbnails
     ------------------------------------------------------------------ */

  function initProductMedia() {
    var thumbs = document.querySelectorAll('[data-media-thumb]');
    var main = document.querySelector('.product-media__img');
    if (!thumbs.length || !main) return;

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var full = thumb.getAttribute('data-media-full');
        if (!full) return;
        main.removeAttribute('srcset');
        main.src = full;
        main.alt = thumb.getAttribute('data-media-alt') || '';
        thumbs.forEach(function (other) { other.classList.remove('is-active'); });
        thumb.classList.add('is-active');
      });
    });
  }

  /* ------------------------------------------------------------------
     Before / after comparison slider
     ------------------------------------------------------------------ */

  function initCompare() {
    document.querySelectorAll('[data-compare]').forEach(function (compare) {
      var stage = compare.querySelector('.compare__stage');
      var clip = compare.querySelector('[data-compare-clip]');
      var handle = compare.querySelector('[data-compare-handle]');
      var range = compare.querySelector('[data-compare-range]');
      var beforeImg = clip ? clip.querySelector('.compare__img') : null;
      if (!stage || !clip) return;

      function setPosition(percent) {
        var value = Math.max(0, Math.min(100, percent));
        clip.style.width = value + '%';
        if (handle) handle.style.left = value + '%';
        if (beforeImg) beforeImg.style.width = stage.offsetWidth + 'px';
        if (range) {
          range.value = value;
          range.setAttribute('aria-valuetext', Math.round(value) + '% of the original photo shown');
        }
      }

      function positionFromEvent(event) {
        var rect = stage.getBoundingClientRect();
        var clientX = event.touches ? event.touches[0].clientX : event.clientX;
        return ((clientX - rect.left) / rect.width) * 100;
      }

      if (range) {
        range.addEventListener('input', function () {
          setPosition(parseFloat(range.value));
        });
      }

      var dragging = false;

      stage.addEventListener('pointerdown', function (event) {
        dragging = true;
        stage.setPointerCapture(event.pointerId);
        setPosition(positionFromEvent(event));
      });

      stage.addEventListener('pointermove', function (event) {
        if (!dragging) return;
        setPosition(positionFromEvent(event));
      });

      ['pointerup', 'pointercancel'].forEach(function (name) {
        stage.addEventListener(name, function () { dragging = false; });
      });

      window.addEventListener('resize', function () {
        setPosition(range ? parseFloat(range.value) : 50);
      });

      // Wait for the image so the clip width matches the rendered stage.
      if (beforeImg && !beforeImg.complete) {
        beforeImg.addEventListener('load', function () { setPosition(50); });
      }
      setPosition(50);
    });
  }

  /* ------------------------------------------------------------------
     Collection sorting — submit on change, no separate button needed
     ------------------------------------------------------------------ */

  function initAutoSubmit() {
    document.querySelectorAll('[data-auto-submit]').forEach(function (form) {
      form.querySelectorAll('select').forEach(function (select) {
        select.addEventListener('change', function () { form.submit(); });
      });
    });
  }

  /* ------------------------------------------------------------------
     Account: address delete confirmation
     ------------------------------------------------------------------ */

  function initAddressDelete() {
    document.querySelectorAll('[data-address-delete]').forEach(function (link) {
      link.addEventListener('click', function (event) {
        event.preventDefault();
        var message = link.getAttribute('data-confirm-message') || 'Delete this address?';
        if (!window.confirm(message)) return;

        var form = document.createElement('form');
        form.method = 'post';
        form.action = link.getAttribute('data-address-url');
        var method = document.createElement('input');
        method.type = 'hidden';
        method.name = '_method';
        method.value = 'delete';
        form.appendChild(method);
        document.body.appendChild(form);
        form.submit();
      });
    });
  }

  /* ------------------------------------------------------------------
     Login page: reveal the password recovery form when linked to
     ------------------------------------------------------------------ */

  function initRecoverToggle() {
    var toggle = document.querySelector('[data-recover-toggle]');
    var target = document.getElementById('recover');
    if (!toggle || !target) return;

    toggle.addEventListener('click', function (event) {
      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      var input = target.querySelector('input');
      if (input) input.focus({ preventScroll: true });
    });
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */

  function init() {
    initMobileNav();
    initNavDropdowns();
    initQuantity();
    initVariantPicker();
    initPhotoSlots();
    initUploader();
    initProductForm();
    initProductMedia();
    initCompare();
    initAutoSubmit();
    initAddressDelete();
    initRecoverToggle();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
